import analytics from '../analytics';
import { CUSTOM_SELECT } from '../classnames/custom-select';
import { RADIO_CONTAINER_SELECTOR } from '../classnames/design-system';
import { PURCHASE_SECTION, SUBSCRIBE_EMAIL_CONTAINER } from '../classnames/home';
import { NEW_WINDOW_LINK } from '../classnames/new-window-link';
import { SIDEBAR_POPUP_CONTAINER } from '../classnames/sidebar-popup';
import { PURCHASE_BAR_ANCHOR_BOTTOM_CLASS, PURCHASE_BAR_ANCHOR_TARGET_CLASS } from '../classnames/purchase-bar-anchor';
import { REVIEWS_SECTION_ID } from '../classnames/reviews';
import { CITATIONS_CONTAINER } from '../classnames/scientific-citations';
import { TOOLTIP_ITEM } from '../classnames/tooltip';
import { UGC_SELECTOR } from '../classnames/ugc-selector';
import { TRUE_MED_WIDGET } from '../classnames/vendors';
import { PRODUCT_CARD } from '../classnames/product-card';
import SessionStorage from '../utils/session-storage';
import { GEOLOCATION_DATA_KEY } from '../utils/session-storage-keys';
import { SELECTORS as ANNOUNCEMENT_BAR_SELECTORS } from '../announcement-bar/constants';
import { SELECTORS as COUNTDOWN_TIMER_SELECTORS } from '../countdown-timer/constants';
import { SELECTORS as CLIPBOARD_COPY_SELECTORS } from '../shared/сlipboard-сopy/constants';
import { SELECTORS as TAB_SELECTORS } from '../shared/tab/constants';
import { SELECTORS as ABOVE_THE_FOLD_SELECTORS } from '../above-the-fold/constants';
import { SELECTORS as PROMO_WITH_VIDEO_SELECTORS } from '../promo-with-video/constants';
import { SELECTORS as REVIEWS_SLIDER_SELECTORS } from '../reviews/slider/constants';
import { SELECTORS as PRODUCT_CARD_OPTIONS_SELECTORS } from '../product-card-options/constants';
import { ATTN_DATA_ATTR } from '../vendors/constants';
import { SELECTORS as ZAPIER_FORM_SELECTORS } from '../zapier-form-handler/constants';

export default class HandlersByClassName {
  bodyClassList = document.body.classList;
  isDomesticCustomer = SessionStorage.get(GEOLOCATION_DATA_KEY)?.isDomestic;

  initializeHandlers() {
    if (document.querySelector(`.${ANNOUNCEMENT_BAR_SELECTORS.CONTAINER}`)) {
      import('../announcement-bar').then(({ AnnouncementBar }) => {
        (new AnnouncementBar()).initialize();
      });
    }

    if (document.querySelector(PURCHASE_BAR_ANCHOR_TARGET_CLASS)
      && document.querySelector(PURCHASE_BAR_ANCHOR_BOTTOM_CLASS)) {
      import('../purchase-bar-anchor').then(({ default: PurchaseBarAnchor }) => {
        (new PurchaseBarAnchor()).initializeEventListeners();
      });
    }

    if (document.getElementsByClassName('swiper')) {
      import('../swiper').then(({ default: Swiper }) => {
        (new Swiper()).initialize();
      });
    }

    if (document.querySelector(`.${REVIEWS_SLIDER_SELECTORS.SLIDER}`)) {
      import('../reviews/slider').then(({ ReviewsSlider }) => {
        const container = document.querySelector(`.${REVIEWS_SLIDER_SELECTORS.SLIDER}`);

        new ReviewsSlider(container).initialize();
      });
    }

    if (document.querySelectorAll(`.${PRODUCT_CARD_OPTIONS_SELECTORS.LIST}`).length > 0) {
      import('../product-card-options').then(({ ProductCardOptions }) => {
        document.querySelectorAll(`.${PRODUCT_CARD_OPTIONS_SELECTORS.LIST}`)
          .forEach((container) => {
            new ProductCardOptions(container).initializeEventListeners();
          });
      });
    }

    if (document.querySelector(`.${PROMO_WITH_VIDEO_SELECTORS.SECTION}`)) {
      import('../promo-with-video').then(({ PromoWithVideo }) => {
        new PromoWithVideo().initialize();
      });
    }

    if (document.querySelectorAll(`.${CLIPBOARD_COPY_SELECTORS.CONTAINER}`)) {
      import('../shared/сlipboard-сopy').then(({ ClipboardCopy }) => {
        new ClipboardCopy().initialize();
      });
    }

    if (document.querySelector(`.${ABOVE_THE_FOLD_SELECTORS.PRODUCT_CARD}`)) {
      import('../above-the-fold').then(({ AboveTheFold }) => {
        const container = document.querySelector(`.${ABOVE_THE_FOLD_SELECTORS.PRODUCT_CARD}`);

        new AboveTheFold(container).initializeEventListeners();
      });
    }

    if (this.bodyClassList.contains('customers/addresses')) {
      import('../addresses/remove-handler').then(({ default: AddressRemoveHandler }) => {
        (new AddressRemoveHandler()).initializeEventListeners();
      });

      import('../addresses/edit-handler').then(({ default: AddressEditHandler }) => {
        (new AddressEditHandler()).initializeEventListeners();
      });

      import('../addresses/create-handler').then(({ default: AddressCreateHandler }) => {
        (new AddressCreateHandler()).initializeEventListeners();
      });
    }

    if (this.bodyClassList.contains('customers/login')) {
      import('../auth/login').then(({ default: AuthLogin }) => {
        (new AuthLogin()).initializeEventListeners();
      });
    }

    if (document.getElementById(REVIEWS_SECTION_ID)) {
      import('../reviews/section-renderer').then(({ ReviewsRenderer }) => {
        (new ReviewsRenderer()).render();
      });
    }

    if (this.bodyClassList.contains('product') || this.bodyClassList.contains('reviews')) {
      analytics.productViewed();
    }

    if (this.bodyClassList.contains('blog')) {
      import('../blog/post-list').then(({ default: BlogPostsList }) => {
        (new BlogPostsList()).initializeEventListeners();
      });
    }

    if (this.bodyClassList.contains('article')) {
      import('../blog/index').then(({ default: Blog }) => {
        (new Blog()).initialize();
      });

      import('../blog/pinterest-button').then(({ default: PinterestButton }) => {
        (new PinterestButton()).initialize();
      });
    }

    if (this.bodyClassList.contains('article') || this.bodyClassList.contains('blog')) {
      import('../lib/youtube-player').then(({ default: YouTubePlayer }) => {
        YouTubePlayer.initialize();
      });
    }

    if (document.querySelectorAll(`.${SIDEBAR_POPUP_CONTAINER}`).length > 0) {
      import('../sidebar-popup').then(({ default: SidebarPopup }) => {
        (new SidebarPopup()).initializeEventListeners();
      });
    }

    if (document.querySelector(`.${CITATIONS_CONTAINER}`)) {
      import('../scientific-citations').then(({ default: ScientificCitations }) => {
        (new ScientificCitations()).render();
      });
    }

    if (document.querySelector(`.${SUBSCRIBE_EMAIL_CONTAINER}`)) {
      import('../home/email-subscription').then(({ default: HomeEmailSubscription }) => {
        (new HomeEmailSubscription()).initialize();
      });
    }

    if (document.getElementById(PURCHASE_SECTION)) {
      import('../home/purchase-section').then(({ default: PurchaseSection }) => {
        (new PurchaseSection()).initializeEventListeners();
      });
    }

    if (this.bodyClassList.contains('index') || this.bodyClassList.contains('home-old')) {
      import('../home/hero-animation').then(({ default: HomeHeroAnimation }) => {
        (new HomeHeroAnimation()).initialize();
      });
    }

    if (document.getElementsByClassName(CUSTOM_SELECT)?.length > 0) {
      import('../design-system/custom-select').then(({ default: CustomSelect }) => {
        (new CustomSelect()).initialize();
      });
    }

    if (document.getElementsByClassName(UGC_SELECTOR).length > 0) {
      import('../ugc-selector').then(({ default: UGCSelector }) => {
        (new UGCSelector()).initialize();
      });
    }

    if (document.querySelectorAll(`.${TAB_SELECTORS.CONTAINER}`).length > 0) {
      import('../shared/tab').then(({ Tab }) => {
        const containers = document.querySelectorAll(`.${TAB_SELECTORS.CONTAINER}`);

        [...containers].forEach((container) => new Tab(container).initializeEventListeners());
      });
    }

    if (document.querySelector(`.${TRUE_MED_WIDGET}`) && this.isDomesticCustomer) {
      import('../vendors/truemed-widget').then(({ default: TrueMedWidget }) => {
        (new TrueMedWidget()).initializeEventListeners();
      });
    }

    if (document.querySelector(`.${NEW_WINDOW_LINK}`)) {
      import('../components/new-window-link').then(({ default: NewWindowLink }) => {
        (new NewWindowLink()).initialize();
      });
    }

    // TODO: Refactor one-click checkout button handler
    //  to initializeEventListeners depending on the one-click checkout button class
    import('../product-card-v3').then(({ ProductCardV3 }) => {
      (new ProductCardV3()).initializeEventListeners();
    });

    if (document.querySelector(`.${PRODUCT_CARD}`)) {
      import('../product-card').then(({ default: ProductCard }) => {
        (new ProductCard()).initializeEventListeners();
      });
    }

    if (document.querySelector('.js-submit-review')) {
      import('../reviews/form').then(({ default: Form }) => {
        (new Form()).initializeEventListeners();
      });
    }

    if (document.getElementById(REVIEWS_SECTION_ID)) {
      import('../shared/above-the-fold/rating-click-handler').then(({ default: RatingClickHandler }) => {
        (new RatingClickHandler()).initializeEventListeners();
      });
    }

    if (document.querySelector('.js-product-review-success-banner')) {
      import('../reviews/success-banner').then(({ default: SuccessBanner }) => {
        (new SuccessBanner()).initializeEventListeners();
      });
    }

    if (document.querySelector(('.js-text-input .input__field'))) {
      import('../design-system/input').then(({ default: Input }) => {
        (new Input()).initializeEventListeners();
      });
    }

    if (document.querySelector(RADIO_CONTAINER_SELECTOR)) {
      import('../design-system/radio').then(({ default: Radio }) => {
        (new Radio()).initializeEventListeners();
      });
    }

    if (document.querySelector(`.${TOOLTIP_ITEM}`)) {
      import('../tooltip/tooltip').then(({ default: Tooltip }) => {
        (new Tooltip()).initializeEventListeners();
      });
    }

    if (document.querySelector('.js-toggle-item-container')) {
      import('../design-system/toggle').then(({ default: Toggle }) => {
        (new Toggle()).initializeEventListeners();
      });
    }

    if (document.querySelector(`.${COUNTDOWN_TIMER_SELECTORS.CONTAINER}`)) {
      import('../countdown-timer').then(({ CountdownTimer }) => {
        (new CountdownTimer(document.querySelector(`.${COUNTDOWN_TIMER_SELECTORS.CONTAINER}`))).initialize();
      });
    }

    if (document.querySelector(`.${ZAPIER_FORM_SELECTORS.FORM_CONTAINER}`)) {
      import('../zapier-form-handler').then(({ ZapierFormHandler }) => {
        (new ZapierFormHandler()).initialize();
      });
    }

    if (document.querySelector(`[${ATTN_DATA_ATTR}="false"]`)) {
      import('../vendors/attentive-popup').then(({ AttentivePopup }) => {
        (new AttentivePopup()).hide();
      });
    }
  }
}
